<template>
    <b-row>
        <h2 class="pl-1">QR-коды</h2>

        <b-col cols="12" class="mb-2">
            <b-row class="align-items-end">
                <b-col md="3" sm="12" class="mb-1">
                    <b-form-group label="Статус" label-for="qr-status-filter" class="mb-0">
                        <b-form-select
                            id="qr-status-filter"
                            v-model="filters.status"
                            :options="statusFilterOptions"
                        />
                    </b-form-group>
                </b-col>
                <b-col md="4" sm="12" class="mb-1">
                    <b-form-group label="Batch ID" label-for="qr-batch-filter" class="mb-0">
                        <b-input-group>
                            <b-form-input
                                id="qr-batch-filter"
                                v-model="filters.batch_id"
                                type="search"
                                placeholder="batch-001"
                            />
                            <b-input-group-append>
                                <b-button :disabled="!filters.batch_id" @click="filters.batch_id = ''">
                                    Очистить
                                </b-button>
                            </b-input-group-append>
                        </b-input-group>
                    </b-form-group>
                </b-col>
                <b-col md="5" sm="12" class="mb-1">
                    <div class="d-flex justify-content-md-end">
                        <b-button
                            variant="outline-primary"
                            class="mr-1"
                            :disabled="isExporting"
                            @click="exportCsv"
                        >
                            Экспорт CSV
                        </b-button>
                        <router-link
                            v-if="isCreateAvailable"
                            class="create__btn btn-primary"
                            :to="{ name: 'qr-code-create' }"
                        >
                            Создать
                        </router-link>
                    </div>
                </b-col>
            </b-row>
        </b-col>

        <b-col cols="12">
            <b-table
                striped
                hover
                responsive
                sort-icon-left
                :busy="isBusy"
                :items="items"
                :fields="fields"
                @sort-changed="handleSortChange"
            >
                <template #table-busy>
                    <div class="text-center text-primary my-2">
                        <b-spinner class="align-middle"></b-spinner>
                        <strong class="ml-1">Идет загрузка ...</strong>
                    </div>
                </template>

                <template #cell(status)="{ item }">
                    {{ getStatusLabel(item.status) }}
                </template>

                <template #cell(used_at)="{ item }">
                    {{ formatDate(item.used_at) }}
                </template>

                <template #cell(created_at)="{ item }">
                    {{ formatDate(item.created_at) }}
                </template>

                <template #cell(updated_at)="{ item }">
                    {{ formatDate(item.updated_at) }}
                </template>

                <template #cell(crud_row)="{ item }">
                    <div class="d-flex float-right">
                        <router-link
                            v-if="isUpdateAvailable"
                            :to="{ name: 'qr-code-update', params: { id: item.id } }"
                        >
                            <b-button variant="outline-success" class="update__btn" size="sm">
                                <feather-icon icon="Edit2Icon" size="18" />
                            </b-button>
                        </router-link>

                        <div class="ml-1" v-if="isDeleteAvailable">
                            <b-button
                                v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                                v-b-modal="`qr-code-delete-${item.id}`"
                                variant="outline-danger"
                                class="delete__btn"
                                size="sm"
                            >
                                <feather-icon icon="MinusCircleIcon" size="18" />
                            </b-button>
                            <b-modal
                                :id="`qr-code-delete-${item.id}`"
                                cancel-title="Отменить"
                                cancel-variant="danger btn-sm"
                                body-class="deactivate-btn_modal"
                                title="Отключение QR-кода"
                                hide-header-close
                                centered
                            >
                                Вы действительно хотите отключить этот QR-код?

                                <template #modal-footer>
                                    <b-button
                                        variant="danger btn-sm"
                                        @click="$bvModal.hide(`qr-code-delete-${item.id}`)"
                                    >
                                        Отменить
                                    </b-button>
                                    <b-button
                                        variant="success btn-sm"
                                        @click="disableEntry(item.id)"
                                    >
                                        Отключить
                                    </b-button>
                                </template>
                            </b-modal>
                        </div>
                    </div>
                </template>
            </b-table>
        </b-col>

        <b-col cols="12" class="mb-3 d-flex justify-content-between align-items-center">
            <b-form-select
                class="float-right col-1"
                v-model="pagination.perPage"
                :options="pagination.perPageOptions"
            />
            <b-pagination
                v-model="pagination.page"
                :total-rows="pagination.total"
                :per-page="pagination.perPage"
                align="center"
                size="sm"
                class="my-0"
            />
        </b-col>
    </b-row>
</template>

<script>
import {
    BTable,
    BRow,
    BCol,
    BFormGroup,
    BPagination,
    BInputGroup,
    BFormInput,
    BInputGroupAppend,
    BButton,
    BSpinner,
    BFormSelect,
    BModal,
} from "bootstrap-vue";
import QrCodes from "@/services/qrCodes";
import Ripple from "vue-ripple-directive";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { paginationData } from "@/util/pagination-helper";
import permissionComputeds from "@/util/permissionComputeds";
import { debounce } from "@/util/helper";

const api = new QrCodes();

export default {
    name: "AppQrCodes",
    components: {
        BTable,
        BRow,
        BCol,
        BFormGroup,
        BPagination,
        BInputGroup,
        BFormInput,
        BInputGroupAppend,
        BButton,
        BSpinner,
        BFormSelect,
        BModal,
        ToastificationContent,
    },
    directives: {
        Ripple,
    },
    data() {
        return {
            isBusy: false,
            isExporting: false,
            filters: {
                status: "",
                batch_id: "",
            },
            statusFilterOptions: [
                { value: "", text: "Все" },
                { value: "new", text: "Новый" },
                { value: "used", text: "Использован" },
                { value: "disabled", text: "Отключен" },
            ],
            fields: [
                { key: "id", label: "ID", sortable: true },
                { key: "code", label: "Код" },
                { key: "status", label: "Статус" },
                { key: "batch_id", label: "Batch ID" },
                { key: "used_at", label: "Использован" },
                { key: "created_at", label: "Дата создания" },
                { key: "updated_at", label: "Дата обновления" },
            ],
            items: [],
            pagination: paginationData(),
        };
    },
    watch: {
        "pagination.page": {
            async handler() {
                this.generateParams();
                await this.getData();
            },
        },
        "pagination.perPage": {
            async handler() {
                this.pagination.page = 1;
                this.generateParams();
                await this.getData();
            },
        },
        "pagination.sorting": {
            async handler() {
                this.pagination.page = 1;
                this.generateParams();
                await this.getData();
            },
        },
        filters: {
            deep: true,
            handler: debounce(async function handler() {
                this.pagination.page = 1;
                this.generateParams();
                await this.getData();
            }, 400),
        },
    },
    async mounted() {
        this.setParams();
        if (this.isDeleteAvailable || this.isUpdateAvailable) {
            this.fields.push({ key: "crud_row", label: "" });
        }
        await this.getData();
    },
    computed: {
        ...permissionComputeds("qr-code"),
    },
    methods: {
        handleSortChange(ctx) {
            if (!ctx.sortBy) {
                this.pagination.sorting = "";
                return;
            }

            const order = ctx.sortDesc ? "desc" : "asc";
            this.pagination.sorting = `${ctx.sortBy},${order}`;
        },
        generateParams() {
            const params = new URLSearchParams(window.location.search);
            params.set("page", this.pagination.page);
            params.set("perPage", this.pagination.perPage);

            if (this.filters.status) params.set("filters[status]", this.filters.status);
            else params.delete("filters[status]");

            if (this.filters.batch_id) params.set("filters[batch_id]", this.filters.batch_id);
            else params.delete("filters[batch_id]");

            if (this.pagination.sorting) {
                const [col, ord] = this.pagination.sorting.split(",");
                params.set("sort[column]", col);
                params.set("sort[direction]", ord);
            } else {
                params.delete("sort[column]");
                params.delete("sort[direction]");
            }

            const url = `${window.location.pathname}?${params.toString()}`;
            window.history.pushState({ path: url }, "", url);
        },
        setParams() {
            const params = new URLSearchParams(window.location.search);
            if (params.has("page")) this.pagination.page = params.get("page");
            if (params.has("perPage")) this.pagination.perPage = params.get("perPage");
            if (params.has("filters[status]")) this.filters.status = params.get("filters[status]");
            if (params.has("filters[batch_id]")) this.filters.batch_id = params.get("filters[batch_id]");
            if (params.has("sort[column]")) {
                this.pagination.sorting = `${params.get("sort[column]")},${params.get("sort[direction]")}`;
            }
        },
        getParams(includePagination = true) {
            const params = new URLSearchParams();
            if (includePagination) {
                params.append("page", this.pagination.page);
                params.append("perPage", this.pagination.perPage);
            }
            if (this.filters.status) params.append("filters[status]", this.filters.status);
            if (this.filters.batch_id) params.append("filters[batch_id]", this.filters.batch_id);
            if (this.pagination.sorting) {
                const [col, ord] = this.pagination.sorting.split(",");
                params.append("sort[column]", col);
                params.append("sort[direction]", ord);
            }
            return params.toString();
        },
        async getData() {
            this.isBusy = true;
            await api.fetchAll(this.getParams())
                .then((res) => {
                    const pagination = res.data.pagination || {};
                    this.items = res.data.data || res.data.items || [];
                    this.pagination.total = res.data.total || pagination.total || 0;
                    this.pagination.page = res.data.current_page || pagination.current_page || this.pagination.page;
                    this.pagination.perPage = res.data.per_page || pagination.per_page || this.pagination.perPage;
                })
                .catch((error) => {
                    console.error(error);
                    this.showToast("danger", "Что-то пошло не так!", "XIcon");
                })
                .finally(() => {
                    this.isBusy = false;
                });
        },
        async disableEntry(id) {
            await api.disable(id)
                .then(() => {
                    this.$bvModal.hide(`qr-code-delete-${id}`);
                    this.showToast("success", "QR-код отключен!", "CheckIcon");
                    this.getData();
                })
                .catch((error) => {
                    console.error(error);
                    this.showToast("danger", "Что-то пошло не так!", "XIcon");
                });
        },
        async exportCsv() {
            this.isExporting = true;
            await api.export(this.getParams(false))
                .then((res) => {
                    const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement("a");
                    link.href = url;
                    link.download = "qr-codes.csv";
                    link.click();
                    window.URL.revokeObjectURL(url);
                })
                .catch((error) => {
                    console.error(error);
                    this.showToast("danger", "Не удалось экспортировать CSV!", "XIcon");
                })
                .finally(() => {
                    this.isExporting = false;
                });
        },
        formatDate(value) {
            return value ? new Date(value).toLocaleString("ru") : "Отсутствует";
        },
        getStatusLabel(status) {
            const option = this.statusFilterOptions.find((el) => el.value === status);
            return option ? option.text : status;
        },
        showToast(variant, text, icon) {
            this.$toast({
                component: ToastificationContent,
                props: {
                    title: text,
                    icon,
                    variant,
                },
            });
        },
    },
};
</script>
